import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Megaphone, RefreshCw, Sparkles, CheckSquare,
  CalendarDays, Send, Clock, Trash2, Settings, ChevronLeft, ChevronRight,
  Copy, Check, Image as ImageIcon, FileText, LayoutGrid, List, X, KeyRound,
  ExternalLink, Calendar as CalendarIcon,
} from 'lucide-react';
import { FaTwitter, FaLinkedin, FaInstagram, FaFacebook, FaYoutube, FaPinterest } from 'react-icons/fa';
import { FaTiktok } from 'react-icons/fa6';
import { format, parse, isValid } from 'date-fns';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import ModuleDemoVideo from '../components/ModuleDemoVideo';

const API = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8002';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

const POST_TEMPLATES = [
  {
    id: 'launch',
    name: 'Product launch',
    accent: 'from-amber-500/30 to-orange-600/10',
    ring: 'border-amber-500/40',
    tone: 'promotional',
    channels: { twitter: true, linkedin: true, instagram: true, facebook: true },
    fill: (t) =>
      `🚀 Introducing ${t}\n\nBuilt for people who want results without the fluff.\n\nWhat you get:\n• Clear value from day one\n• Simple setup\n• Support that actually helps\n\nReady to try it? Drop a comment or DM.\n\n#launch #new #${t.replace(/\s+/g, '').slice(0, 18)}`,
    imagePrompt: (t) => `Premium product launch visual for "${t}", clean studio light, bold focal subject, modern lifestyle brand aesthetic, square social crop`,
  },
  {
    id: 'offer',
    name: 'Limited offer',
    accent: 'from-rose-500/25 to-pink-700/10',
    ring: 'border-rose-500/40',
    tone: 'promotional',
    channels: { twitter: true, linkedin: false, instagram: true, facebook: true },
    fill: (t) =>
      `⏳ Limited offer: ${t}\n\nFor a short window only — don’t wait on this one.\n\n✓ Easy to claim\n✓ Real savings\n✓ Ends soon\n\nTap through / reply “INFO” and we’ll help you lock it in.\n\n#deal #offer #limited`,
    imagePrompt: (t) => `Eye-catching limited-time offer graphic for "${t}", high contrast, scarce urgency without clutter, square ad creative`,
  },
  {
    id: 'tip',
    name: 'Quick tip',
    accent: 'from-sky-500/25 to-cyan-700/10',
    ring: 'border-sky-500/40',
    tone: 'educational',
    channels: { twitter: true, linkedin: true, instagram: true, facebook: false },
    fill: (t) =>
      `💡 Quick tip: ${t}\n\nMost people overcomplicate this. Try this instead:\n\n1) Start with one clear goal\n2) Do the smallest useful step today\n3) Measure what actually moved\n\nSave this for later — and share with someone who’d find it useful.\n\n#tips #learn #growth`,
    imagePrompt: (t) => `Clean educational tip illustration about "${t}", soft gradient backdrop, single strong visual metaphor, square social post`,
  },
  {
    id: 'story',
    name: 'Behind scenes',
    accent: 'from-violet-500/20 to-fuchsia-700/10',
    ring: 'border-violet-500/40',
    tone: 'casual',
    channels: { twitter: true, linkedin: true, instagram: true, facebook: true },
    fill: (t) =>
      `👀 Behind the scenes: ${t}\n\nA little of what we’re working on this week — the messy middle, not just the highlight reel.\n\nWhat’s one thing you’re building right now?\n\n#behindthescenes #buildinpublic`,
    imagePrompt: (t) => `Warm candid behind-the-scenes photo vibe for "${t}", natural light, authentic workspace mood, square crop`,
  },
  {
    id: 'proof',
    name: 'Social proof',
    accent: 'from-emerald-500/25 to-teal-700/10',
    ring: 'border-emerald-500/40',
    tone: 'professional',
    channels: { twitter: true, linkedin: true, instagram: false, facebook: true },
    fill: (t) =>
      `⭐ Customer win: ${t}\n\n“We finally found a workflow that sticks.”\n\nResults like this don’t happen by accident — clear process + consistent follow-through.\n\nWant the same playbook? Reply and we’ll share how.\n\n#testimonial #results #customersuccess`,
    imagePrompt: (t) => `Trust-building social proof visual for "${t}", confident professional aesthetic, subtle celebration, square format`,
  },
  {
    id: 'event',
    name: 'Event invite',
    accent: 'from-indigo-500/25 to-blue-800/10',
    ring: 'border-indigo-500/40',
    tone: 'professional',
    channels: { twitter: true, linkedin: true, instagram: true, facebook: true },
    fill: (t) =>
      `📅 You’re invited: ${t}\n\nJoin us for a focused session — practical takeaways you can use the same day.\n\nWho it’s for: builders, operators, and anyone ready to level up.\n\nSave the date and bring a friend.\n\n#event #community #invite`,
    imagePrompt: (t) => `Elegant event invitation visual for "${t}", atmospheric lighting, minimal typography space, square social banner`,
  },
  {
    id: 'carousel',
    name: 'Carousel hook',
    accent: 'from-yellow-500/20 to-amber-800/10',
    ring: 'border-yellow-500/35',
    tone: 'educational',
    channels: { twitter: false, linkedin: true, instagram: true, facebook: false },
    fill: (t) =>
      `${t}\n\nSwipe-worthy takeaways:\n\n→ Hook them in line 1\n→ One idea per frame\n→ End with a clear CTA\n\nWhich slide would you open with?\n\n#carousel #content #marketing`,
    imagePrompt: (t) => `Bold carousel cover slide for "${t}", large readable focal shape, modern marketing design, square 1:1`,
  },
  {
    id: 'reengage',
    name: 'Re-engage',
    accent: 'from-zinc-400/20 to-zinc-700/10',
    ring: 'border-zinc-500/40',
    tone: 'casual',
    channels: { twitter: true, linkedin: true, instagram: false, facebook: true },
    fill: (t) =>
      `Quick check-in 👋\n\nStill thinking about ${t}?\n\nHere’s a simple next step if you want momentum this week — no overload, just one move.\n\nReply “GO” and I’ll send the short version.\n\n#followup #community`,
    imagePrompt: (t) => `Friendly re-engagement visual tied to "${t}", approachable brand colors, calm optimistic mood, square social`,
  },
];

const CHANNELS = [
  {
    id: 'twitter', label: 'X / Twitter', Icon: FaTwitter, color: '#e7e9ea', canPublish: true, limit: 280,
    helpUrl: 'https://developer.twitter.com/en/portal/dashboard',
    keys: [
      { key: 'TWITTER_API_KEY', label: 'API Key' },
      { key: 'TWITTER_API_SECRET', label: 'API Secret' },
      { key: 'TWITTER_ACCESS_TOKEN', label: 'Access Token' },
      { key: 'TWITTER_ACCESS_TOKEN_SECRET', label: 'Access Token Secret' },
    ],
  },
  {
    id: 'linkedin', label: 'LinkedIn', Icon: FaLinkedin, color: '#0A66C2', canPublish: true, limit: 3000,
    helpUrl: 'https://www.linkedin.com/developers/apps',
    keys: [
      { key: 'LINKEDIN_ACCESS_TOKEN', label: 'Access Token' },
      { key: 'LINKEDIN_PERSON_URN', label: 'Person URN (urn:li:person:…)' },
    ],
  },
  {
    id: 'instagram', label: 'Instagram', Icon: FaInstagram, color: '#E1306C', canPublish: true, limit: 2200,
    helpUrl: 'https://developers.facebook.com/',
    keys: [
      { key: 'INSTAGRAM_BUSINESS_ID', label: 'Business Account ID' },
      { key: 'INSTAGRAM_ACCESS_TOKEN', label: 'Access Token' },
    ],
  },
  {
    id: 'facebook', label: 'Facebook', Icon: FaFacebook, color: '#1877F2', canPublish: true, limit: 5000,
    helpUrl: 'https://developers.facebook.com/',
    keys: [
      { key: 'FACEBOOK_PAGE_ID', label: 'Page ID' },
      { key: 'FACEBOOK_PAGE_ACCESS_TOKEN', label: 'Page Access Token' },
    ],
  },
  {
    id: 'tiktok', label: 'TikTok', Icon: FaTiktok, color: '#fff', canPublish: false, limit: 2200,
    helpUrl: 'https://developers.tiktok.com/',
    keys: [
      { key: 'TIKTOK_ACCESS_TOKEN', label: 'Access Token' },
      { key: 'TIKTOK_OPEN_ID', label: 'Open ID' },
    ],
  },
  {
    id: 'youtube', label: 'YouTube', Icon: FaYoutube, color: '#FF0000', canPublish: false, limit: 5000,
    helpUrl: 'https://studio.youtube.com/',
    keys: [
      { key: 'YOUTUBE_CHANNEL_ID', label: 'Channel ID' },
    ],
  },
  {
    id: 'threads', label: 'Threads', Icon: FaTwitter, color: '#fff', canPublish: false, limit: 500,
    helpUrl: 'https://developers.facebook.com/docs/threads',
    keys: [
      { key: 'THREADS_ACCESS_TOKEN', label: 'Access Token' },
      { key: 'THREADS_USER_ID', label: 'User ID' },
    ],
  },
  {
    id: 'pinterest', label: 'Pinterest', Icon: FaPinterest, color: '#E60023', canPublish: false, limit: 500,
    helpUrl: 'https://developers.pinterest.com/',
    keys: [
      { key: 'PINTEREST_ACCESS_TOKEN', label: 'Access Token' },
      { key: 'PINTEREST_BOARD_ID', label: 'Board ID' },
    ],
  },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

/** Calendar date + time picker → value kept as `YYYY-MM-DDTHH:mm` local (same as datetime-local). */
function ScheduleDateTimePicker({ value, onChange }) {
  const parsed = value ? parse(value, "yyyy-MM-dd'T'HH:mm", new Date()) : null;
  const selectedDate = parsed && isValid(parsed) ? parsed : undefined;
  const hour = selectedDate ? format(selectedDate, 'HH') : '10';
  const minute = selectedDate ? format(selectedDate, 'mm') : '00';
  const nearestMin = MINUTES.includes(minute) ? minute : '00';
  const [open, setOpen] = useState(false);

  const apply = (date, h = hour, m = nearestMin) => {
    if (!date) {
      onChange('');
      return;
    }
    const next = new Date(date);
    next.setHours(Number(h), Number(m), 0, 0);
    onChange(format(next, "yyyy-MM-dd'T'HH:mm"));
  };

  return (
    <div data-testid="ai-ads-schedule-at">
      <label className="text-[11px] text-zinc-500 mb-1 block">Schedule time</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-left inline-flex items-center gap-2 hover:border-zinc-500"
          >
            <CalendarIcon className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className={selectedDate ? 'text-zinc-100' : 'text-zinc-500'}>
              {selectedDate
                ? format(selectedDate, 'EEE, MMM d · h:mm a')
                : 'Pick date & time on calendar'}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto p-3 bg-zinc-900 border-zinc-700 text-zinc-100 shadow-xl"
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              if (d) apply(d);
            }}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            className="rounded-md"
          />
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-700">
            <Clock className="w-3.5 h-3.5 text-zinc-400" />
            <select
              value={hour}
              onChange={(e) => apply(selectedDate || new Date(), e.target.value, nearestMin)}
              className="h-8 px-2 rounded-md bg-zinc-950 border border-zinc-700 text-xs"
            >
              {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className="text-zinc-500">:</span>
            <select
              value={nearestMin}
              onChange={(e) => apply(selectedDate || new Date(), hour, e.target.value)}
              className="h-8 px-2 rounded-md bg-zinc-950 border border-zinc-700 text-xs"
            >
              {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto h-8 px-3 rounded-md text-xs font-medium bg-amber-500 text-zinc-950"
            >
              Done
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Per-channel API key forms for social publish credentials. */
function ChannelConnectModal({ open, onClose, creds, onSaved, initialChannel }) {
  const [active, setActive] = useState(initialChannel || 'twitter');
  const [values, setValues] = useState({});
  const [masked, setMasked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initialChannel) setActive(initialChannel);
  }, [open, initialChannel]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/settings/api-keys`, { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        const map = {};
        (data.keys || []).forEach((k) => { map[k.key] = k.is_set ? (k.value || '••••••••') : ''; });
        setMasked(map);
        setValues({});
      } catch {
        /* ignore */
      }
    })();
  }, [open]);

  if (!open) return null;
  const ch = CHANNELS.find((c) => c.id === active) || CHANNELS[0];
  const Icon = ch.Icon;
  const configured = !!creds[ch.id]?.configured;

  const save = async () => {
    const updates = {};
    (ch.keys || []).forEach(({ key }) => {
      const v = (values[key] || '').trim();
      if (v && !v.startsWith('•')) updates[key] = v;
    });
    if (!Object.keys(updates).length) {
      toast.error('Enter at least one new key value to save');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/settings/api-keys`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(`Saved ${data.updated?.length || 0} key(s) for ${ch.label}`);
      setValues({});
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" data-testid="ai-ads-connect-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-900 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h3 className="text-base font-semibold inline-flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-400" /> Connect channels
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">Paste API keys per network to enable posting from AI Ads.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-800">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row min-h-0 flex-1 overflow-hidden">
          <div className="sm:w-48 border-b sm:border-b-0 sm:border-r border-zinc-800 p-2 overflow-y-auto flex sm:flex-col gap-1">
            {CHANNELS.map((c) => {
              const on = !!creds[c.id]?.configured;
              const CIcon = c.Icon;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActive(c.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg text-xs inline-flex items-center gap-2 ${
                    active === c.id ? 'bg-amber-500/15 border border-amber-500/40 text-amber-50' : 'hover:bg-zinc-800 text-zinc-300 border border-transparent'
                  }`}
                >
                  <CIcon style={{ color: c.color, width: 14, height: 14 }} />
                  <span className="truncate flex-1">{c.label}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                </button>
              );
            })}
          </div>
          <div className="flex-1 p-5 overflow-y-auto space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Icon style={{ color: ch.color, width: 20, height: 20 }} />
                <div>
                  <div className="font-medium text-sm">{ch.label}</div>
                  <div className={`text-[11px] ${configured ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {configured ? 'Keys configured' : 'Not connected — add keys below'}
                    {!ch.canPublish ? ' · manual / coming soon for auto-post' : ''}
                  </div>
                </div>
              </div>
              {ch.helpUrl && (
                <a href={ch.helpUrl} target="_blank" rel="noreferrer" className="text-[11px] text-amber-300 hover:underline inline-flex items-center gap-1">
                  Get keys <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {(ch.keys || []).map(({ key, label }) => (
              <div key={key}>
                <label className="text-[11px] text-zinc-500 mb-1 block">{label}</label>
                <input
                  type="password"
                  autoComplete="off"
                  placeholder={masked[key] ? `Saved: ${masked[key]}` : 'Paste key…'}
                  value={values[key] || ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm"
                  data-testid={`ai-ads-key-${key}`}
                />
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="h-9 px-4 rounded-lg text-xs font-semibold bg-amber-500 text-zinc-950 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="ai-ads-connect-save"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                Save {ch.label} keys
              </button>
              <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-xs border border-zinc-700 text-zinc-300">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  draft: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function dayKey(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function postDayKey(post) {
  const raw = post.scheduled_at || post.published_at || post.created_at;
  if (!raw) return null;
  return dayKey(new Date(raw));
}

function parseHashtags(text) {
  return Array.from((text || '').matchAll(/#([A-Za-z0-9_]+)/g)).map((m) => m[1]);
}

/* ─── Create Ads (Holo-simple UX over AI Ads planner) ─────────────────────── */
/** Spoken language for Sora video ads (Indian + common) */
const VIDEO_LANGUAGES = [
  { id: 'en', label: 'English' },
  { id: 'en-in', label: 'Indian English' },
  { id: 'hi', label: 'Hindi' },
  { id: 'bn', label: 'Bengali' },
  { id: 'ta', label: 'Tamil' },
  { id: 'te', label: 'Telugu' },
  { id: 'mr', label: 'Marathi' },
  { id: 'gu', label: 'Gujarati' },
  { id: 'kn', label: 'Kannada' },
  { id: 'ml', label: 'Malayalam' },
  { id: 'pa', label: 'Punjabi' },
  { id: 'ur', label: 'Urdu' },
  { id: 'or', label: 'Odia' },
  { id: 'as', label: 'Assamese' },
  { id: 'es', label: 'Spanish' },
  { id: 'fr', label: 'French' },
  { id: 'ar', label: 'Arabic' },
  { id: 'zh', label: 'Chinese (Mandarin)' },
];

/** Friendly styles shown to users — map to backend agent ids */
const AD_STYLES = [
  {
    id: 'ad_factory',
    label: 'UGC video ads',
    blurb: 'Script + storyboard + image ideas — like a creator filmed it',
    platforms: { google: false, meta: true },
    accent: 'from-fuchsia-500/25 to-violet-600/10',
  },
  {
    id: 'paid_social',
    label: 'Facebook & Instagram',
    blurb: 'Feed ads with hooks, headlines, and audience angles',
    platforms: { google: false, meta: true },
    accent: 'from-pink-500/25 to-rose-700/10',
  },
  {
    id: 'ppc',
    label: 'Google search ads',
    blurb: 'Headlines, descriptions, and keywords ready to paste',
    platforms: { google: true, meta: false },
    accent: 'from-sky-500/25 to-blue-700/10',
  },
  {
    id: 'creative',
    label: 'Headline pack',
    blurb: 'Google RSA + Meta headlines, angles, and testing plan (copy kit)',
    platforms: { google: true, meta: true },
    accent: 'from-amber-500/25 to-orange-700/10',
  },
  {
    id: 'full',
    label: 'Full campaign',
    blurb: 'Google + Meta copy, keywords, structure, funnel, and launch checklist',
    platforms: { google: true, meta: true },
    accent: 'from-emerald-500/20 to-teal-700/10',
  },
];

function brandFromUrl(raw) {
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const host = (u.hostname || '').replace(/^www\./, '');
    if (!host) return '';
    const base = host.split('.')[0] || host;
    return base.charAt(0).toUpperCase() + base.slice(1);
  } catch {
    return '';
  }
}

function formatAdsKitText(active) {
  const body = active?.plan || {};
  const campaign = body.campaign || {};
  const factory = body.factory || {};
  const lines = [];
  lines.push(`# ${campaign.name || active?.business || 'Ads kit'}`);
  lines.push(`Agent: ${active?.agent || body.agent || 'full'} · Source: ${body.source || '—'}`);
  if (body.summary) lines.push('', body.summary);
  if (campaign.budget_guidance) lines.push('', `Budget: ${campaign.budget_guidance}`);
  if (campaign.structure_notes) lines.push('', `Structure: ${campaign.structure_notes}`);
  if (campaign.bidding_notes) lines.push('', `Bidding: ${campaign.bidding_notes}`);
  if (campaign.targeting_notes) lines.push('', `Targeting: ${campaign.targeting_notes}`);
  if ((campaign.funnel || []).length) {
    lines.push('', 'Funnel:');
    campaign.funnel.forEach((f) => lines.push(`- ${f}`));
  }
  if ((campaign.keywords || []).length) lines.push('', `Keywords: ${(campaign.keywords || []).join(', ')}`);
  if ((campaign.negative_keywords || []).length) {
    lines.push(`Negatives: ${(campaign.negative_keywords || []).join(', ')}`);
  }
  if (factory.unique_mechanism || factory.script) {
    lines.push('', '## Ad Factory');
    if (factory.unique_mechanism) lines.push(`Unique mechanism: ${factory.unique_mechanism}`);
    if (factory.avatar) lines.push(`Avatar: ${factory.avatar}`);
    if (factory.offer_brief) lines.push(`Offer brief: ${factory.offer_brief}`);
    if (factory.market_research_summary) lines.push('', factory.market_research_summary);
    if ((factory.necessary_beliefs || []).length) {
      lines.push('', 'Necessary beliefs:');
      factory.necessary_beliefs.forEach((b) => lines.push(`- ${b}`));
    }
    if (factory.script) lines.push('', '### Script', factory.script);
    if ((factory.storyboard || []).length) {
      lines.push('', '### Storyboard');
      factory.storyboard.forEach((s) => {
        lines.push(`Segment ${s.segment || ''} (${s.duration_sec || '?'}s)`);
        if (s.visual) lines.push(`  Visual: ${s.visual}`);
        if (s.voiceover) lines.push(`  VO: ${s.voiceover}`);
        if (s.on_screen_text) lines.push(`  Text: ${s.on_screen_text}`);
      });
    }
    if ((factory.image_prompts || []).length) {
      lines.push('', '### Image prompts');
      factory.image_prompts.forEach((p) => lines.push(`- ${p}`));
    }
    if ((factory.video_prompts || []).length) {
      lines.push('', '### Video prompts (external Sora/Veo/editor)');
      factory.video_prompts.forEach((p) => lines.push(`- ${p}`));
    }
  }
  (body.variants || []).forEach((v) => {
    lines.push('', `## ${String(v.platform || 'platform').toUpperCase()}`);
    if (v.cta) lines.push(`CTA: ${v.cta}`);
    if (v.primary_text) lines.push('', v.primary_text);
    if ((v.headlines || []).length) {
      lines.push('', 'Headlines:');
      v.headlines.forEach((h) => lines.push(`- ${h}`));
    }
    if ((v.descriptions || []).length) {
      lines.push('', 'Descriptions:');
      v.descriptions.forEach((d) => lines.push(`- ${d}`));
    }
    if ((v.angles || []).length) lines.push('', `Angles: ${(v.angles || []).join('; ')}`);
    if ((v.sitelinks || []).length) lines.push(`Sitelinks: ${(v.sitelinks || []).join('; ')}`);
  });
  if ((body.testing_plan || []).length) {
    lines.push('', '## Testing plan');
    body.testing_plan.forEach((t) => lines.push(`- ${t}`));
  }
  if ((body.checklist || []).length) {
    lines.push('', '## Launch checklist');
    body.checklist.forEach((c) => lines.push(`- [ ] ${c}`));
  }
  return lines.join('\n');
}

function PaidAdsKitsPanel({ initialPlanId = '' }) {
  const [url, setUrl] = useState('');
  const [business, setBusiness] = useState('');
  const [offer, setOffer] = useState('');
  const [audience, setAudience] = useState('');
  const [goal, setGoal] = useState('leads');
  const [showMore, setShowMore] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [styleId, setStyleId] = useState('ad_factory');
  const [submitting, setSubmitting] = useState(false);
  const [active, setActive] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [kitCopied, setKitCopied] = useState(false);
  const [stillBusy, setStillBusy] = useState('');
  const [stills, setStills] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoSpoken, setVideoSpoken] = useState('');
  const [videoLanguage, setVideoLanguage] = useState('en');
  const [postBusy, setPostBusy] = useState(false);
  const [postTargets, setPostTargets] = useState({ instagram: true, facebook: true });
  const [ideaIndex, setIdeaIndex] = useState(0);

  const style = AD_STYLES.find((s) => s.id === styleId) || AD_STYLES[0];

  const loadPlans = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/ai-ads/plans?limit=12`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Could not load plans');
      const data = await resp.json();
      setPlans(data.plans || []);
    } catch {
      setPlans([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const createPlan = async (e) => {
    e.preventDefault();
    const brand = business.trim() || brandFromUrl(url.trim());
    if (!brand && !url.trim()) {
      toast.error('Add your website or brand name');
      return;
    }
    const platforms = style.platforms || { google: true, meta: true };
    const selected = Object.entries(platforms).filter(([, on]) => on).map(([k]) => k);
    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/api/ai-ads/plans`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          business: brand || 'My brand',
          offer: offer.trim(),
          audience: audience.trim(),
          goal: goal.trim() || 'leads',
          budget_note: '',
          url: url.trim(),
          platforms: selected.length ? selected : ['meta'],
          agent: style.id,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data.detail;
        const msg = typeof detail === 'object' ? detail.message || detail.detail : detail;
        throw new Error(msg || 'Could not create ads');
      }
      setActive(data.plan);
      setStills(data.plan?.plan?.factory?.stills || []);
      setVideos(data.plan?.plan?.factory?.videos || []);
      setVideoSpoken(data.plan?.plan?.factory?.script || '');
      setVideoLanguage(data.plan?.plan?.factory?.video_language || 'en');
      setVideoDuration(8);
      setIdeaIndex(0);
      setShowAdvanced(false);
      if (!business.trim() && brand) setBusiness(brand);
      loadPlans();
      toast.success('Your ads are ready — swipe through ideas below');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openPlan = async (id) => {
    try {
      const resp = await fetch(`${API}/api/ai-ads/plans/${id}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Not found');
      const data = await resp.json();
      setActive(data.plan);
      setStills(data.plan?.plan?.factory?.stills || []);
      setVideos(data.plan?.plan?.factory?.videos || []);
      setVideoSpoken(data.plan?.plan?.factory?.script || '');
      setVideoLanguage(data.plan?.plan?.factory?.video_language || 'en');
      setVideoDuration(8);
      setIdeaIndex(0);
      setShowAdvanced(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (!initialPlanId || loadingList) return;
    openPlan(initialPlanId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlanId, loadingList]);

  const generateStill = async (prompt, idx = 0) => {
    if (!prompt?.trim()) return;
    setStillBusy(String(idx));
    try {
      const resp = await fetch(`${API}/api/ai-ads/generate-image`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prompt: prompt.trim(),
          plan_id: active?.id || null,
          content_hint: (active?.plan?.factory?.script || active?.plan?.summary || '').slice(0, 400),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Image failed');
      const img = data.public_url || data.image_url;
      if (!img) throw new Error('No image URL');
      setStills((prev) => [...prev, { url: img, prompt: prompt.trim() }].slice(-8));
      toast.success('Image ready');
    } catch (err) {
      toast.error(err.message || 'Image failed');
    } finally {
      setStillBusy('');
    }
  };

  const copyKit = async () => {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(formatAdsKitText(active));
      setKitCopied(true);
      toast.success('Copied — paste into Ads Manager');
      setTimeout(() => setKitCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const generateVideo = async () => {
    if (!active?.id) {
      toast.error('Generate ads first');
      return;
    }
    const duration = [4, 8, 12, 16, 20, 24, 30].includes(videoDuration) ? videoDuration : 8;
    setVideoBusy(true);
    try {
      const resp = await fetch(`${API}/api/ai-ads/generate-video`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          plan_id: active.id,
          duration,
          size: '720x1280',
          spoken_text: videoSpoken.trim(),
          language: videoLanguage || 'en',
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Video generation failed';
        if (/openai|sora|404|settings/i.test(detail)) {
          throw new Error(`${detail} → Settings → API Keys → OpenAI API Key`);
        }
        throw new Error(detail);
      }
      if (data.plan) setActive(data.plan);
      const list = data.plan?.plan?.factory?.videos || [];
      if (list.length) setVideos(list);
      else if (data.public_url || data.video_url) {
        setVideos((prev) => [...prev, { url: data.public_url || data.video_url }].slice(-6));
      }
      toast.success('Video ready — post to Instagram / Facebook below');
    } catch (err) {
      toast.error(err.message || 'Video failed');
    } finally {
      setVideoBusy(false);
    }
  };

  const publishVideoAd = async () => {
    if (!active?.id) return;
    const platforms = Object.entries(postTargets).filter(([, on]) => on).map(([k]) => k);
    if (!platforms.length) {
      toast.error('Pick Instagram and/or Facebook');
      return;
    }
    if (!videos.length && !stills.length) {
      toast.error('Make a video (or image) first');
      return;
    }
    setPostBusy(true);
    try {
      const resp = await fetch(`${API}/api/ai-ads/publish`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          plan_id: active.id,
          platforms,
          use_video: videos.length > 0,
          use_image: videos.length === 0,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Publish failed');
      const ok = (data.results || []).filter((r) => r.success);
      const fail = (data.results || []).filter((r) => !r.success);
      if (ok.length) toast.success(`Posted to ${ok.map((r) => r.platform).join(', ')}`);
      fail.forEach((r) => toast.error(`${r.platform}: ${r.message}`));
    } catch (err) {
      toast.error(err.message || 'Publish failed');
    } finally {
      setPostBusy(false);
    }
  };

  const body = active?.plan || {};
  const campaign = body.campaign || {};
  const factory = body.factory || {};
  const styleLabel = AD_STYLES.find((s) => s.id === (active?.agent || body.agent))?.label || 'Your ads';
  const mediaSrc = (u) => {
    if (!u) return '';
    if (u.startsWith('http') || u.startsWith('data:')) return u;
    return `${API}${u.startsWith('/') ? '' : '/'}${u}`;
  };

  // Build swipeable "idea" cards from results
  const ideaCards = useMemo(() => {
    const cards = [];
    if (factory.script) {
      cards.push({
        key: 'script',
        kind: 'UGC script',
        title: 'Talking-head ad script',
        body: factory.script,
      });
    }
    (factory.storyboard || []).forEach((s, i) => {
      cards.push({
        key: `shot-${i}`,
        kind: 'Storyboard',
        title: `Shot ${s.segment || i + 1}${s.duration_sec ? ` · ${s.duration_sec}s` : ''}`,
        body: [s.visual && `Visual: ${s.visual}`, s.voiceover && `Say: ${s.voiceover}`, s.on_screen_text && `On screen: ${s.on_screen_text}`]
          .filter(Boolean)
          .join('\n'),
      });
    });
    const agentMode = active?.agent || body.agent || '';
    const headlineLimit = agentMode === 'creative' ? 15 : agentMode === 'full' ? 12 : 8;
    (body.variants || []).forEach((v) => {
      const lines = [];
      if (v.primary_text) lines.push(v.primary_text);
      const heads = v.headlines || [];
      if (heads.length) {
        lines.push(agentMode === 'creative' ? 'Headlines:' : '');
        heads.slice(0, headlineLimit).forEach((h) => lines.push(`• ${h}`));
      }
      (v.descriptions || []).slice(0, agentMode === 'creative' || agentMode === 'full' ? 4 : 2).forEach((d) => lines.push(d));
      (v.angles || []).slice(0, 5).forEach((a) => lines.push(`Angle: ${a}`));
      cards.push({
        key: `var-${v.platform}`,
        kind: v.platform === 'google' ? 'Google ad' : v.platform === 'meta' ? 'Meta ad' : String(v.platform),
        title: `${String(v.platform || 'Ad').charAt(0).toUpperCase()}${String(v.platform || 'ad').slice(1)} copy`,
        body: lines.filter(Boolean).join('\n\n'),
        cta: v.cta,
      });
    });
    if ((agentMode === 'full' || agentMode === 'ppc') && (campaign.structure_notes || campaign.bidding_notes || (campaign.funnel || []).length)) {
      cards.push({
        key: 'structure',
        kind: 'Campaign plan',
        title: 'Structure & bidding',
        body: [
          campaign.structure_notes && `Structure: ${campaign.structure_notes}`,
          campaign.bidding_notes && `Bidding: ${campaign.bidding_notes}`,
          (campaign.funnel || []).length && `Funnel:\n${campaign.funnel.map((f) => `• ${f}`).join('\n')}`,
          (campaign.keywords || []).length && `Keywords: ${(campaign.keywords || []).slice(0, 10).join(', ')}`,
        ].filter(Boolean).join('\n\n'),
      });
    }
    if (!cards.length && body.summary) {
      cards.push({ key: 'summary', kind: 'Overview', title: campaign.name || 'Campaign', body: body.summary });
    }
    return cards;
  }, [
    active?.agent,
    body.agent,
    body.variants,
    body.summary,
    campaign.name,
    campaign.structure_notes,
    campaign.bidding_notes,
    campaign.funnel,
    campaign.keywords,
    factory.script,
    factory.storyboard,
  ]);

  const currentIdea = ideaCards[ideaIndex] || null;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="ai-ads-paid-kits">
      {/* Hero — Holo-simple */}
      <div className="text-center space-y-3 pt-2">
        <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/30">
          AI for ads
        </span>
        <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-white">
          Turn your website into ads
        </h2>
        <p className="text-zinc-400 text-sm md:text-base max-w-xl mx-auto">
          Website → ad ideas → AI video → post to Instagram & Facebook.
        </p>
        <ol className="flex flex-wrap justify-center gap-2 text-[11px] text-zinc-500 pt-1">
          <li className="px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800">1. Brand</li>
          <li className="px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800">2. Style</li>
          <li className="px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800">3. Video</li>
          <li className="px-2.5 py-1 rounded-full bg-zinc-900 border border-zinc-800">4. Post</li>
        </ol>
      </div>

      <form onSubmit={createPlan} className="rounded-3xl border border-zinc-800 bg-zinc-900/50 p-5 md:p-7 space-y-5 shadow-xl shadow-black/20" data-testid="ai-ads-form">
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Website URL</label>
          <input
            value={url}
            onChange={(e) => {
              const v = e.target.value;
              setUrl(v);
              if (!business.trim()) {
                const guessed = brandFromUrl(v);
                if (guessed) setBusiness(guessed);
              }
            }}
            placeholder="https://yourbrand.com"
            className="w-full h-12 px-4 rounded-2xl bg-zinc-950 border border-zinc-700 text-sm focus:border-fuchsia-500/50 focus:outline-none"
            data-testid="ai-ads-url"
            disabled={submitting}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Brand name</label>
          <input
            value={business}
            onChange={(e) => setBusiness(e.target.value)}
            placeholder="Filled from your URL if empty"
            className="w-full h-11 px-4 rounded-2xl bg-zinc-950 border border-zinc-700 text-sm"
            data-testid="ai-ads-business"
            disabled={submitting}
          />
        </div>

        <div data-testid="ai-ads-styles">
          <p className="text-xs font-medium text-zinc-400 mb-2">What do you want to create?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {AD_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyleId(s.id)}
                disabled={submitting}
                className={`text-left rounded-2xl border p-3.5 bg-gradient-to-br ${s.accent} transition disabled:opacity-50 ${
                  styleId === s.id ? 'border-fuchsia-400/60 ring-1 ring-fuchsia-400/30' : 'border-zinc-700/80 hover:border-zinc-600'
                }`}
                data-testid={`ai-ads-style-${s.id}`}
              >
                <span className="block text-sm font-semibold text-zinc-50">{s.label}</span>
                <span className="block text-[11px] text-zinc-400 mt-1 leading-snug">{s.blurb}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="text-xs text-zinc-500 hover:text-zinc-300"
        >
          {showMore ? 'Hide optional details' : 'Optional: offer, audience, goal'}
        </button>
        {showMore && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={offer} onChange={(e) => setOffer(e.target.value)} className="h-10 px-3 rounded-xl bg-zinc-950 border border-zinc-700 text-sm" placeholder="What are you selling?" disabled={submitting} />
            <input value={audience} onChange={(e) => setAudience(e.target.value)} className="h-10 px-3 rounded-xl bg-zinc-950 border border-zinc-700 text-sm" placeholder="Who is it for?" disabled={submitting} />
            <select value={goal} onChange={(e) => setGoal(e.target.value)} className="h-10 px-3 rounded-xl bg-zinc-950 border border-zinc-700 text-sm sm:col-span-2" disabled={submitting}>
              <option value="leads">Get leads</option>
              <option value="sales">Get sales</option>
              <option value="traffic">More website visits</option>
              <option value="awareness">Brand awareness</option>
            </select>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="ai-ads-start"
          className="w-full h-12 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-violet-600 via-fuchsia-500 to-rose-500 hover:opacity-95 disabled:opacity-50 inline-flex items-center justify-center gap-2 shadow-lg shadow-fuchsia-900/30"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {submitting ? 'Creating your ads…' : 'Generate my ads'}
        </button>
      </form>

      {/* Results — swipe ideas */}
      {active && (
        <div className="space-y-4" data-testid="ai-ads-result">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs text-fuchsia-300/90">{styleLabel}</p>
              <h3 className="text-lg font-semibold">{campaign.name || active.business}</h3>
            </div>
            <button
              type="button"
              onClick={copyKit}
              className="h-9 px-3 rounded-full text-xs font-medium border border-zinc-600 text-zinc-200 inline-flex items-center gap-1.5 hover:bg-zinc-800"
              data-testid="ai-ads-copy-kit"
            >
              {kitCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              Copy all
            </button>
          </div>

          {currentIdea ? (
            <div className="relative rounded-3xl border border-zinc-700 bg-zinc-900/70 p-6 min-h-[220px] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                  {currentIdea.kind}
                </span>
                <span className="text-[11px] text-zinc-500">
                  {ideaIndex + 1} / {ideaCards.length}
                </span>
              </div>
              <h4 className="text-base font-semibold text-white mb-2">{currentIdea.title}</h4>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap flex-1 leading-relaxed">{currentIdea.body}</p>
              {currentIdea.cta && (
                <p className="text-xs text-zinc-500 mt-3">Suggested CTA: {currentIdea.cta}</p>
              )}
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-800">
                <button
                  type="button"
                  disabled={ideaIndex <= 0}
                  onClick={() => setIdeaIndex((i) => Math.max(0, i - 1))}
                  className="h-10 px-3 rounded-full border border-zinc-700 text-zinc-300 text-xs inline-flex items-center gap-1 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" /> Skip back
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(currentIdea.body);
                      toast.success('Idea copied');
                    } catch {
                      toast.error('Copy failed');
                    }
                  }}
                  className="h-10 px-4 rounded-full bg-white text-zinc-950 text-xs font-semibold"
                >
                  Save / copy this
                </button>
                <button
                  type="button"
                  disabled={ideaIndex >= ideaCards.length - 1}
                  onClick={() => setIdeaIndex((i) => Math.min(ideaCards.length - 1, i + 1))}
                  className="h-10 px-3 rounded-full border border-zinc-700 text-zinc-300 text-xs inline-flex items-center gap-1 disabled:opacity-30"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/5 p-4 space-y-4" data-testid="ai-ads-produce-post">
            <div>
              <p className="text-sm font-medium text-zinc-100">Produce video & post</p>
              <p className="text-xs text-zinc-500 mt-1">
                Videos use your brand, offer, and script from this plan (not random). Edit what they say and pick length below.
                Needs OpenAI Sora in{' '}
                <a href="/settings?tab=api-keys" className="text-fuchsia-300 hover:underline">Settings → API Keys</a>.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-[11px] text-zinc-500">Length (Sora)</label>
                <div className="flex flex-wrap gap-2" data-testid="ai-ads-video-duration">
                  {[
                    { sec: 4, label: '4s' },
                    { sec: 8, label: '8s' },
                    { sec: 12, label: '12s' },
                    { sec: 16, label: '16s' },
                    { sec: 20, label: '20s' },
                    { sec: 24, label: '24s' },
                    { sec: 30, label: '~30s' },
                  ].map(({ sec, label }) => (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => setVideoDuration(sec)}
                      disabled={videoBusy || postBusy}
                      title={sec >= 24 ? 'Built with Sora create + extend (takes longer)' : undefined}
                      className={`h-9 px-3 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                        videoDuration === sec
                          ? 'bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-100'
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {videoDuration >= 24 && (
                  <p className="text-[10px] text-zinc-600">
                    {videoDuration}s uses Sora extend (stitched clips) — expect a longer wait.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] text-zinc-500" htmlFor="ai-ads-video-language">
                  Language
                </label>
                <select
                  id="ai-ads-video-language"
                  data-testid="ai-ads-video-language"
                  value={videoLanguage}
                  onChange={(e) => setVideoLanguage(e.target.value)}
                  disabled={videoBusy || postBusy}
                  className="w-full h-9 rounded-xl bg-zinc-950 border border-zinc-700 px-3 text-sm text-zinc-100 focus:outline-none focus:border-fuchsia-500/60 disabled:opacity-50"
                >
                  {VIDEO_LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] text-zinc-500" htmlFor="ai-ads-video-spoken">
                What should be said in the video
              </label>
              <textarea
                id="ai-ads-video-spoken"
                data-testid="ai-ads-video-spoken"
                value={videoSpoken}
                onChange={(e) => setVideoSpoken(e.target.value)}
                disabled={videoBusy || postBusy}
                rows={4}
                maxLength={1200}
                placeholder="Write the lines in the language you selected (Hindi, Bengali, English, …)."
                className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-fuchsia-500/60 disabled:opacity-50"
              />
              <p className="text-[10px] text-zinc-600">
                Prefer writing the script in {VIDEO_LANGUAGES.find((l) => l.id === videoLanguage)?.label || 'that language'}.
                Keep it short for {videoDuration}s. Sora approximates speech — results vary by language.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={generateVideo}
                disabled={videoBusy || postBusy}
                className="h-10 px-4 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="ai-ads-generate-video"
              >
                {videoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {videoBusy ? `Rendering ${videoDuration}s video…` : `Make ${videoDuration}s video ad`}
              </button>
              {(factory.image_prompts || []).slice(0, 1).map((p, i) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => generateStill(p, i)}
                  disabled={!!stillBusy || videoBusy}
                  className="h-10 px-3 rounded-full text-xs font-medium border border-zinc-600 text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {stillBusy === String(i) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                  Or make image
                </button>
              ))}
            </div>
            {videos.length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] text-zinc-500">Your video</p>
                <video
                  src={mediaSrc(videos[videos.length - 1].url)}
                  controls
                  className="w-full max-w-xs rounded-xl border border-zinc-700 bg-black aspect-[9/16] max-h-80"
                />
              </div>
            )}
            {stills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {stills.map((s) => (
                  <a key={s.url} href={mediaSrc(s.url)} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-xl overflow-hidden border border-zinc-700">
                    <img src={mediaSrc(s.url)} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-zinc-800">
              {['instagram', 'facebook'].map((p) => (
                <label key={p} className="inline-flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!postTargets[p]}
                    onChange={() => setPostTargets((prev) => ({ ...prev, [p]: !prev[p] }))}
                  />
                  <span className="capitalize">{p}</span>
                </label>
              ))}
              <button
                type="button"
                onClick={publishVideoAd}
                disabled={postBusy || videoBusy || (!videos.length && !stills.length)}
                className="ml-auto h-10 px-4 rounded-full text-xs font-semibold bg-emerald-500 text-zinc-950 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="ai-ads-publish-video"
              >
                {postBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Post now
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            {showAdvanced ? 'Hide extra details' : 'Show keywords, checklist & more'}
          </button>
          {showAdvanced && (
            <div className="space-y-3 text-sm text-zinc-300 rounded-2xl border border-zinc-800 p-4">
              {body.summary && <p>{body.summary}</p>}
              {(campaign.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {campaign.keywords.map((k) => (
                    <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-950 border border-zinc-700">{k}</span>
                  ))}
                </div>
              )}
              {(factory.necessary_beliefs || []).length > 0 && (
                <ul className="space-y-1 text-xs text-zinc-400">
                  {factory.necessary_beliefs.map((b) => <li key={b}>• {b}</li>)}
                </ul>
              )}
              {(factory.video_prompts || []).length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Video prompts (for Sora / Veo / CapCut)</p>
                  <ul className="space-y-1 text-xs text-zinc-400">
                    {factory.video_prompts.map((p) => <li key={p}>→ {p}</li>)}
                  </ul>
                </div>
              )}
              {(body.checklist || []).length > 0 && (
                <ul className="space-y-1 text-xs">
                  {body.checklist.map((c) => <li key={c}>□ {c}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent */}
      <div className="rounded-2xl border border-zinc-800/80 p-4">
        <p className="text-xs font-medium text-zinc-500 mb-2">Recent</p>
        {loadingList ? (
          <p className="text-xs text-zinc-600">Loading…</p>
        ) : plans.length === 0 ? (
          <p className="text-xs text-zinc-600">Nothing yet — generate your first set above.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {plans.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => openPlan(p.id)}
                className={`text-left rounded-full border px-3 py-1.5 text-[11px] ${
                  active?.id === p.id ? 'border-fuchsia-500/50 bg-fuchsia-500/10 text-fuchsia-100' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                {p.business}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Social scheduler (Postiz-like) ──────────────────────────────────────── */
function SocialSchedulerPanel() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [posts, setPosts] = useState([]);
  const [creds, setCreds] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar'); // calendar | queue
  const [selectedDay, setSelectedDay] = useState(() => dayKey(new Date()));

  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState({ twitter: true, linkedin: true, instagram: false, facebook: false });
  const [scheduleAt, setScheduleAt] = useState('');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectChannel, setConnectChannel] = useState('twitter');

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const start = weekStart.toISOString();
      const end = addDays(weekStart, 7).toISOString();
      const [calRes, credRes, statsRes, queueRes] = await Promise.all([
        fetch(`${API}/api/social/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`, { headers: authHeaders() }),
        fetch(`${API}/api/social/credentials`, { headers: authHeaders() }),
        fetch(`${API}/api/social/stats`, { headers: authHeaders() }),
        fetch(`${API}/api/social/scheduled?limit=80&status=pending,draft,published,failed`, { headers: authHeaders() }),
      ]);
      const calPosts = calRes.ok ? ((await calRes.json()).posts || []) : [];
      const queuePosts = queueRes.ok ? (await queueRes.json()) : [];
      const map = new Map();
      [...(Array.isArray(queuePosts) ? queuePosts : []), ...calPosts].forEach((p) => {
        if (p?.id) map.set(p.id, p);
      });
      setPosts(Array.from(map.values()));
      if (credRes.ok) setCreds(await credRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch {
      toast.error('Could not load social queue');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      if (['cancelled'].includes(p.status)) return;
      const k = postDayKey(p);
      if (!k) return;
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    return map;
  }, [posts]);

  const channelList = useMemo(
    () => Object.entries(selectedChannels).filter(([, on]) => on).map(([id]) => id),
    [selectedChannels],
  );

  const charCount = content.length;
  const tightestLimit = Math.min(
    ...CHANNELS.filter((c) => selectedChannels[c.id]).map((c) => c.limit),
    5000,
  );

  const toggleChannel = (id) => {
    setSelectedChannels((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const applyTemplate = (tpl, { withAi = false } = {}) => {
    const subject = topic.trim() || tpl.name;
    setActiveTemplate(tpl.id);
    setTone(tpl.tone || tone);
    if (tpl.channels) {
      setSelectedChannels((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.keys(prev).map((k) => [k, !!tpl.channels[k]])),
      }));
    }
    setContent(tpl.fill(subject));
    setImagePrompt(tpl.imagePrompt(subject));
    if (!topic.trim()) setTopic(subject);
    if (withAi) {
      const platforms = Object.entries(tpl.channels || {})
        .filter(([, on]) => on)
        .map(([id]) => id);
      setTimeout(() => generate({
        seedTopic: subject,
        seedTone: tpl.tone || tone,
        platforms: platforms.length ? platforms : undefined,
      }), 0);
    } else {
      toast.success(`${tpl.name} template loaded — edit or Generate image`);
    }
  };

  const generate = async (opts = {}) => {
    const seedTopic = (opts.seedTopic || topic).trim();
    const platforms = (opts.platforms && opts.platforms.length) ? opts.platforms : channelList;
    if (!seedTopic) {
      toast.error('Enter a topic to generate');
      return;
    }
    if (!platforms.length) {
      toast.error('Select at least one channel');
      return;
    }
    setBusy('generate');
    try {
      const resp = await fetch(`${API}/api/social/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          topic: seedTopic,
          tone: opts.seedTone || tone,
          platforms,
          hashtag_count: 5,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Generate failed');
      const first = (data.posts || [])[0];
      if (!first) throw new Error('No copy returned');
      const tags = (first.hashtags || []).map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ');
      setContent(`${first.content || ''}${tags ? `\n\n${tags}` : ''}`.trim());
      if (!imagePrompt.trim()) {
        setImagePrompt(`Beautiful square social media image for: ${seedTopic}`);
      }
      toast.success(data.source === 'llm' ? 'AI draft ready' : 'Draft ready (template)');
    } catch (err) {
      toast.error(err.message || 'Generate failed');
    } finally {
      setBusy('');
    }
  };

  const generateImage = async () => {
    const prompt = (imagePrompt.trim() || topic.trim() || content.trim().slice(0, 200));
    if (!prompt || prompt.length < 3) {
      toast.error('Add a topic, caption, or image prompt first');
      return;
    }
    setBusy('image');
    try {
      const resp = await fetch(`${API}/api/social/generate-image`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          prompt,
          style: activeTemplate || 'social_media',
          content_hint: content.trim().slice(0, 400),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Image generation failed');
      const url = data.public_url || data.image_url;
      if (!url) throw new Error('No image URL returned');
      setImageUrl(url);
      toast.success('AI image ready — it will post with your caption');
    } catch (err) {
      toast.error(err.message || 'Image generation failed');
    } finally {
      setBusy('');
    }
  };

  const mediaPreviewSrc = useMemo(() => {
    const u = (imageUrl || '').trim();
    if (!u) return '';
    if (u.startsWith('http') || u.startsWith('data:')) return u;
    return `${API}${u.startsWith('/') ? '' : '/'}${u}`;
  }, [imageUrl]);

  const publishNow = async () => {
    if (!content.trim()) {
      toast.error('Write a post first');
      return;
    }
    if (!channelList.length) {
      toast.error('Select at least one channel');
      return;
    }
    setBusy('publish');
    try {
      const resp = await fetch(`${API}/api/social/publish`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          platforms: channelList,
          content: content.trim(),
          hashtags: parseHashtags(content),
          image_url: imageUrl.trim() || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === 'string' ? data.detail : data.detail?.message || 'Publish failed');
      const ok = (data.results || []).filter((r) => r.success).length;
      const fail = (data.results || []).filter((r) => !r.success);
      if (ok) toast.success(`Published to ${ok} channel(s)`);
      fail.forEach((r) => toast.error(`${r.platform}: ${r.message}`));
      setContent('');
      setImageUrl('');
      setImagePrompt('');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Publish failed');
    } finally {
      setBusy('');
    }
  };

  const schedulePost = async () => {
    if (!content.trim()) {
      toast.error('Write a post first');
      return;
    }
    if (!channelList.length) {
      toast.error('Select at least one channel');
      return;
    }
    if (!scheduleAt) {
      toast.error('Pick a schedule time');
      return;
    }
    const iso = new Date(scheduleAt).toISOString();
    setBusy('schedule');
    try {
      const resp = await fetch(`${API}/api/social/schedule`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          platforms: channelList,
          content: content.trim(),
          hashtags: parseHashtags(content),
          image_url: imageUrl.trim() || null,
          scheduled_time: iso,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Schedule failed');
      toast.success(data.message || 'Scheduled');
      setContent('');
      setImageUrl('');
      setImagePrompt('');
      setScheduleAt('');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Schedule failed');
    } finally {
      setBusy('');
    }
  };

  const saveDraft = async () => {
    if (!content.trim()) {
      toast.error('Write a post first');
      return;
    }
    setBusy('draft');
    try {
      const resp = await fetch(`${API}/api/social/drafts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          platforms: channelList.length ? channelList : ['twitter'],
          content: content.trim(),
          hashtags: parseHashtags(content),
          image_url: imageUrl.trim() || null,
          scheduled_time: scheduleAt ? new Date(scheduleAt).toISOString() : null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Draft failed');
      toast.success('Draft saved');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Draft failed');
    } finally {
      setBusy('');
    }
  };

  const cancelPost = async (id) => {
    try {
      const resp = await fetch(`${API}/api/social/scheduled/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Cancel failed');
      }
      toast.success('Removed from queue');
      loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyContent = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const dayPosts = postsByDay[selectedDay] || [];
  const queue = posts
    .filter((p) => ['pending', 'draft', 'failed', 'published'].includes(p.status))
    .sort((a, b) => String(b.scheduled_at || b.created_at).localeCompare(String(a.scheduled_at || a.created_at)));

  return (
    <div className="space-y-5" data-testid="ai-ads-social-scheduler">
      {/* Stats + channels */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Scheduled', value: stats?.pending ?? '—', tone: 'text-amber-300' },
          { label: 'Drafts', value: stats?.drafts ?? '—', tone: 'text-sky-300' },
          { label: 'Published', value: stats?.published ?? '—', tone: 'text-emerald-300' },
          { label: 'Failed', value: stats?.failed ?? '—', tone: 'text-rose-300' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">
            <div className={`text-2xl font-semibold ${s.tone}`}>{s.value}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <ChannelConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        creds={creds}
        initialChannel={connectChannel}
        onSaved={() => { loadAll(); }}
      />

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="text-sm font-semibold">Channels</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setConnectChannel('twitter'); setConnectOpen(true); }}
              data-testid="ai-ads-open-connect"
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-amber-500 text-zinc-950 inline-flex items-center gap-1.5"
            >
              <KeyRound className="w-3.5 h-3.5" /> Connect / API keys
            </button>
            <button
              type="button"
              onClick={() => navigate('/settings?tab=api-keys')}
              className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
            >
              <Settings className="w-3.5 h-3.5" /> Settings
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map(({ id, label, Icon, color }) => {
            const c = creds[id] || {};
            const on = !!selectedChannels[id];
            const connected = !!c.configured;
            return (
              <div key={id} className="inline-flex items-stretch rounded-full border border-zinc-700 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleChannel(id)}
                  data-testid={`ai-ads-channel-${id}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    on ? 'bg-amber-500/10 text-amber-50' : 'bg-zinc-950 text-zinc-400'
                  }`}
                  title={connected ? 'Selected for this post' : 'Select for this post'}
                >
                  <Icon style={{ color, width: 14, height: 14 }} />
                  {label}
                  <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                </button>
                <button
                  type="button"
                  onClick={() => { setConnectChannel(id); setConnectOpen(true); }}
                  className="px-2 text-[10px] border-l border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-amber-200"
                  title={`Authenticate ${label}`}
                  data-testid={`ai-ads-connect-${id}`}
                >
                  {connected ? 'Keys' : 'Connect'}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-zinc-500 mt-2">
          Green dot = API keys saved. Use <strong className="font-medium text-zinc-400">Connect</strong> on each channel to paste keys.
          Auto-publish today: X, LinkedIn, Facebook, Instagram. Others: save keys + copy/post manually for now.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Composer */}
        <div className="xl:col-span-2 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 space-y-3" data-testid="ai-ads-composer">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-400" /> Composer
            </h3>
            <span className={`text-xs font-mono ${charCount > tightestLimit ? 'text-rose-400' : 'text-zinc-500'}`}>
              {charCount}/{tightestLimit}
            </span>
          </div>

          <div data-testid="ai-ads-templates">
            <p className="text-[11px] text-zinc-500 mb-2">Templates — click to fill the post (topic optional)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {POST_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => applyTemplate(tpl)}
                  onDoubleClick={() => applyTemplate(tpl, { withAi: true })}
                  disabled={!!busy}
                  title="Click to fill · double-click to fill + AI polish"
                  className={`relative text-left rounded-xl border p-2.5 bg-gradient-to-br ${tpl.accent} transition hover:scale-[1.02] disabled:opacity-50 ${
                    activeTemplate === tpl.id ? tpl.ring : 'border-zinc-700/80'
                  }`}
                  data-testid={`ai-ads-template-${tpl.id}`}
                >
                  <span className="block text-[11px] font-semibold text-zinc-100 leading-tight">{tpl.name}</span>
                  <span className="block text-[10px] text-zinc-400 mt-0.5 capitalize">{tpl.tone}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="AI topic — e.g. weekend grocery deals"
              className="flex-1 h-9 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm"
              data-testid="ai-ads-topic"
            />
            <select value={tone} onChange={(e) => setTone(e.target.value)} className="h-9 px-2 rounded-lg bg-zinc-950 border border-zinc-700 text-xs">
              <option value="professional">Pro</option>
              <option value="casual">Casual</option>
              <option value="promotional">Promo</option>
              <option value="educational">Edu</option>
            </select>
            <button
              type="button"
              onClick={() => generate()}
              disabled={!!busy}
              className="h-9 px-3 rounded-lg text-xs font-medium bg-amber-500/20 border border-amber-500/40 text-amber-100 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="ai-ads-generate"
            >
              {busy === 'generate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              AI
            </button>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            placeholder="Write your post… Use #hashtags — or pick a template above"
            className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-sm resize-y min-h-[160px]"
            data-testid="ai-ads-content"
          />

          <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-medium text-zinc-300 inline-flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5 text-amber-400" /> Post image
              </p>
              <button
                type="button"
                onClick={generateImage}
                disabled={!!busy}
                className="h-8 px-2.5 rounded-lg text-[11px] font-medium bg-violet-500/20 border border-violet-500/40 text-violet-100 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="ai-ads-generate-image"
              >
                {busy === 'image' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Generate with AI
              </button>
            </div>
            <input
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="Image prompt (auto-filled from template / topic)"
              className="w-full h-9 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm"
              data-testid="ai-ads-image-prompt"
            />
            <div className="flex items-start gap-2">
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Or paste image URL — Instagram needs a public URL"
                className="flex-1 h-9 px-3 rounded-lg bg-zinc-950 border border-zinc-700 text-sm"
                data-testid="ai-ads-image-url"
              />
              {imageUrl ? (
                <button type="button" onClick={() => setImageUrl('')} className="h-9 px-2 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white" title="Clear image">
                  <X className="w-3.5 h-3.5" />
                </button>
              ) : null}
            </div>
            {mediaPreviewSrc ? (
              <div className="relative rounded-lg overflow-hidden border border-zinc-700 aspect-square max-w-[180px] bg-zinc-900">
                <img src={mediaPreviewSrc} alt="Post preview" className="w-full h-full object-cover" />
              </div>
            ) : (
              <p className="text-[10px] text-zinc-500">AI images attach automatically for Post now / Schedule. Instagram & Facebook need a publicly reachable URL.</p>
            )}
          </div>

          <ScheduleDateTimePicker value={scheduleAt} onChange={setScheduleAt} />

          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={publishNow} disabled={!!busy} data-testid="ai-ads-post-now" className="h-9 px-3 rounded-lg text-xs font-semibold bg-emerald-500 text-zinc-950 inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === 'publish' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Post now
            </button>
            <button type="button" onClick={schedulePost} disabled={!!busy} data-testid="ai-ads-schedule" className="h-9 px-3 rounded-lg text-xs font-semibold bg-amber-500 text-zinc-950 inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === 'schedule' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
              Schedule
            </button>
            <button type="button" onClick={saveDraft} disabled={!!busy} className="h-9 px-3 rounded-lg text-xs font-medium border border-zinc-600 text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-50">
              Save draft
            </button>
            <button type="button" onClick={copyContent} className="h-9 px-3 rounded-lg text-xs font-medium border border-zinc-700 text-zinc-300 inline-flex items-center gap-1.5">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              Copy
            </button>
          </div>
        </div>

        {/* Calendar / queue */}
        <div className="xl:col-span-3 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-zinc-700 overflow-hidden">
              <button type="button" onClick={() => setView('calendar')} className={`h-9 px-3 text-xs inline-flex items-center gap-1.5 ${view === 'calendar' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> Calendar
              </button>
              <button type="button" onClick={() => setView('queue')} className={`h-9 px-3 text-xs inline-flex items-center gap-1.5 ${view === 'queue' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}>
                <List className="w-3.5 h-3.5" /> Queue
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setWeekStart((w) => addDays(w, -7))} className="p-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-sm text-zinc-300 min-w-[160px] text-center">
                {weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' – '}
                {addDays(weekStart, 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
              <button type="button" onClick={() => setWeekStart((w) => addDays(w, 7))} className="p-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setWeekStart(startOfWeek(new Date()))} className="h-9 px-2 rounded-lg border border-zinc-700 text-xs text-zinc-300">Today</button>
              <button type="button" onClick={loadAll} className="h-9 px-2 rounded-lg border border-zinc-700 text-xs text-zinc-300 inline-flex items-center gap-1">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {view === 'calendar' ? (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid="ai-ads-calendar">
              <div className="grid grid-cols-7 border-b border-zinc-800">
                {weekDays.map((d) => {
                  const key = dayKey(d);
                  const count = (postsByDay[key] || []).length;
                  const active = key === selectedDay;
                  const isToday = key === dayKey(new Date());
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedDay(key)}
                      className={`px-2 py-3 text-left border-r border-zinc-800 last:border-r-0 min-h-[88px] ${active ? 'bg-amber-500/10' : 'hover:bg-zinc-800/50'}`}
                    >
                      <div className={`text-[10px] uppercase tracking-wider ${isToday ? 'text-amber-300' : 'text-zinc-500'}`}>
                        {d.toLocaleDateString(undefined, { weekday: 'short' })}
                      </div>
                      <div className="text-lg font-semibold mt-0.5">{d.getDate()}</div>
                      <div className="text-[10px] text-zinc-500 mt-1">{count} post{count === 1 ? '' : 's'}</div>
                    </button>
                  );
                })}
              </div>
              <div className="p-4 space-y-2 max-h-[420px] overflow-y-auto">
                <div className="text-xs text-zinc-500 mb-2 inline-flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {selectedDay}
                </div>
                {dayPosts.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-6 text-center">No posts this day — schedule one from the composer.</p>
                ) : dayPosts.map((p) => {
                  const ch = CHANNELS.find((c) => c.id === p.platform);
                  const Icon = ch?.Icon || FaTwitter;
                  return (
                    <div key={p.id} className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                        <Icon style={{ color: ch?.color || '#fff', width: 14, height: 14 }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}>{p.status}</span>
                          <span className="text-[10px] text-zinc-500">
                            {p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : '—'}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-200 whitespace-pre-wrap line-clamp-3">{p.content}</p>
                        {p.message && <p className="text-[11px] text-zinc-500 mt-1">{p.message}</p>}
                      </div>
                      {(p.status === 'pending' || p.status === 'draft') && (
                        <button type="button" onClick={() => cancelPost(p.id)} className="p-1.5 text-zinc-500 hover:text-rose-400" title="Cancel">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-2 max-h-[560px] overflow-y-auto" data-testid="ai-ads-queue">
              {loading ? (
                <p className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
              ) : queue.length === 0 ? (
                <p className="text-sm text-zinc-500 py-8 text-center">Queue is empty.</p>
              ) : queue.map((p) => {
                const ch = CHANNELS.find((c) => c.id === p.platform);
                const Icon = ch?.Icon || FaTwitter;
                return (
                  <div key={p.id} className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-3 flex gap-3">
                    <Icon style={{ color: ch?.color || '#fff', width: 16, height: 16, marginTop: 4 }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-zinc-300">{ch?.label || p.platform}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}>{p.status}</span>
                        <span className="text-[10px] text-zinc-500">
                          {p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : new Date(p.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-200 whitespace-pre-wrap line-clamp-2">{p.content}</p>
                    </div>
                    {(p.status === 'pending' || p.status === 'draft') && (
                      <button type="button" onClick={() => cancelPost(p.id)} className="p-1.5 text-zinc-500 hover:text-rose-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Page shell ──────────────────────────────────────────────────────────── */
const AiAdsDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan') || '';
  const tabFromUrl = searchParams.get('tab') || '';
  const [tab, setTab] = useState(
    tabFromUrl === 'social' || searchParams.get('post') ? 'social' : 'paid',
  ); // paid = create ads | social = post & schedule

  useEffect(() => {
    if (tabFromUrl === 'social' || searchParams.get('post')) setTab('social');
    else if (planFromUrl || tabFromUrl === 'paid') setTab('paid');
  }, [tabFromUrl, planFromUrl, searchParams]);

  return (
    <div className="min-h-screen md:pl-64 p-6 md:p-10 bg-zinc-950 text-white" data-testid="ai-ads-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/marketing-suite')}
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-fuchsia-400" />
              Ads Studio
            </h1>
            <p className="text-zinc-400 text-sm">
              Create ads from your brand — then schedule social posts when you are ready.
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-full border border-zinc-700 p-1 bg-zinc-900/80">
          <button
            type="button"
            onClick={() => setTab('paid')}
            data-testid="ai-ads-tab-paid"
            className={`h-9 px-4 rounded-full text-xs font-semibold ${tab === 'paid' ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            Create ads
          </button>
          <button
            type="button"
            onClick={() => setTab('social')}
            data-testid="ai-ads-tab-social"
            className={`h-9 px-4 rounded-full text-xs font-semibold ${tab === 'social' ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            Post & schedule
          </button>
        </div>
      </header>

      <ModuleDemoVideo moduleKey="ai-ads" className="mb-6" />

      {tab === 'social' ? <SocialSchedulerPanel /> : <PaidAdsKitsPanel initialPlanId={planFromUrl} />}
    </div>
  );
};

export default AiAdsDashboard;
